import React, { useMemo, useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";

import { appApi } from "../../../../store/appApi";
import {
  createNetplusModemEntryApi,
  updateNetplusModemEntryApi,
} from "../../../../services/netplusModem";
import {
  BUCKETS,
  MOVEMENTS,
  zeroBucket,
  bucketTotal,
  num,
  toDateInputValue,
} from "./config";

const fieldClass =
  "w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20";
const labelClass =
  "mb-1 block text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500";

const cloneBucket = (b) => ({
  inactive: num(b?.inactive),
  suspended: num(b?.suspended),
  terminated: num(b?.terminated),
});

const EntryModal = ({
  show,
  onClose,
  entry,
  isFirstEver,
  prevClosing,
  onSaved,
}) => {
  const dispatch = useDispatch();
  const isEdit = Boolean(entry);
  const isOpening = isEdit ? entry.isOpening : isFirstEver;

  const [date, setDate] = useState(toDateInputValue());
  const [opening, setOpening] = useState(zeroBucket());
  const [recovered, setRecovered] = useState(zeroBucket());
  const [recharge, setRecharge] = useState(zeroBucket());
  const [added, setAdded] = useState(zeroBucket());
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setDate(toDateInputValue(entry?.date || new Date()));
    setOpening(cloneBucket(entry?.opening));
    setRecovered(cloneBucket(entry?.recovered));
    setRecharge(cloneBucket(entry?.recharge));
    setAdded(cloneBucket(entry?.added));
    setNote(entry?.note || "");
  }, [show, entry]);

  const carriedIn = useMemo(() => {
    if (isOpening) return null;
    return cloneBucket(isEdit ? entry.opening : prevClosing);
  }, [isOpening, isEdit, entry, prevClosing]);

  const preview = useMemo(() => {
    if (isOpening) return { closing: cloneBucket(opening), netValue: 0 };
    const closing = zeroBucket();
    BUCKETS.forEach(({ key }) => {
      closing[key] =
        num(carriedIn?.[key]) -
        num(recovered[key]) -
        num(recharge[key]) +
        num(added[key]);
    });
    return {
      closing,
      netValue: bucketTotal(closing) - bucketTotal(carriedIn),
    };
  }, [isOpening, opening, carriedIn, recovered, recharge, added]);

  const setBucketField = (setter) => (key, value) =>
    setter((prev) => ({ ...prev, [key]: value === "" ? "" : num(value) }));

  const stateFor = (k) =>
    k === "recovered" ? recovered : k === "recharge" ? recharge : added;
  const setterFor = (k) =>
    k === "recovered" ? setRecovered : k === "recharge" ? setRecharge : setAdded;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = isOpening
        ? { date, opening: cloneBucket(opening), note }
        : {
            date,
            recovered: cloneBucket(recovered),
            recharge: cloneBucket(recharge),
            added: cloneBucket(added),
            note,
          };
      if (isEdit) await updateNetplusModemEntryApi(entry._id, payload);
      else await createNetplusModemEntryApi(payload);

      dispatch(appApi.util.invalidateTags(["NetplusModem"]));
      Swal.fire({
        icon: "success",
        title: isEdit ? "Entry updated" : "Entry saved",
        timer: 1300,
        showConfirmButton: false,
      });
      onSaved?.();
      onClose();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Could not save",
        text:
          err?.response?.data?.message ||
          "Something went wrong while saving this entry.",
      });
    } finally {
      setSaving(false);
    }
  };

  const net = preview.netValue;
  const netCls =
    net > 0 ? "text-rose-600" : net < 0 ? "text-emerald-600" : "text-gray-400";
  const netBg =
    net > 0
      ? "bg-rose-50 border-rose-200"
      : net < 0
        ? "bg-emerald-50 border-emerald-200"
        : "bg-gray-50 border-gray-200";

  return (
    <Modal
      show={show}
      onHide={onClose}
      size="lg"
      centered
      contentClassName="border-0 rounded-4 overflow-hidden"
    >
      <form onSubmit={handleSubmit}>
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4">
          <div>
            <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-amber-700">
              {isOpening ? "Opening" : "Daily"} Entry
            </span>
            <h4 className="mt-2 mb-0 text-xl font-semibold text-gray-900">
              {isEdit ? "Edit entry" : isOpening ? "Declare opening balance" : "Record the day's movements"}
            </h4>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
            aria-label="Close"
          >
            <i className="fa-solid fa-xmark text-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[65vh] overflow-y-auto px-6 py-5">
          <div className="mb-5 max-w-[220px]">
            <label className={labelClass}>Date</label>
            <input
              type="date"
              className={fieldClass}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>

          {isOpening ? (
            <>
              <p className="mb-3 text-sm text-gray-500">
                Enter the starting modem count in each bucket — the ledger
                carries forward from here.
              </p>
              <div className="grid gap-3 sm:grid-cols-3">
                {BUCKETS.map(({ key, label }) => (
                  <div
                    key={key}
                    className="rounded-2xl border border-gray-200 bg-gray-50 p-3"
                  >
                    <label className={labelClass}>{label}</label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      inputMode="numeric"
                      className={`${fieldClass} bg-white text-center text-lg font-semibold`}
                      value={opening[key]}
                      onChange={(e) =>
                        setBucketField(setOpening)(key, e.target.value)
                      }
                    />
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between rounded-2xl border border-gray-200 bg-white px-4 py-3">
                <span className="text-sm font-medium text-gray-500">
                  Opening total
                </span>
                <span className="text-xl font-semibold text-gray-900 tabular-nums">
                  {bucketTotal(opening)}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="overflow-hidden rounded-2xl border border-gray-200">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-gray-500">
                      <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider">
                        Movement
                      </th>
                      {BUCKETS.map((b) => (
                        <th
                          key={b.key}
                          className="px-3 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider"
                        >
                          {b.label}
                        </th>
                      ))}
                      <th className="px-3 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr className="bg-white">
                      <td className="px-4 py-3 text-gray-500">
                        Carried in
                        <span className="ml-1 text-xs text-gray-400">
                          (prev. close)
                        </span>
                      </td>
                      {BUCKETS.map((b) => (
                        <td
                          key={b.key}
                          className="px-3 py-3 text-center text-gray-600 tabular-nums"
                        >
                          {num(carriedIn?.[b.key])}
                        </td>
                      ))}
                      <td className="px-3 py-3 text-center font-semibold text-gray-700 tabular-nums">
                        {bucketTotal(carriedIn)}
                      </td>
                    </tr>

                    {MOVEMENTS.map((mv) => {
                      const state = stateFor(mv.key);
                      const setter = setterFor(mv.key);
                      const chip =
                        mv.sign < 0
                          ? "bg-rose-50 text-rose-600 ring-1 ring-rose-200"
                          : "bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200";
                      return (
                        <tr key={mv.key} className="bg-white">
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-800">
                                {mv.label}
                              </span>
                              <span
                                className={`rounded-full px-1.5 text-xs font-bold ${chip}`}
                              >
                                {mv.sign < 0 ? "−" : "+"}
                              </span>
                            </div>
                            <div className="text-[11px] text-gray-400">
                              {mv.hint}
                            </div>
                          </td>
                          {BUCKETS.map((b) => (
                            <td key={b.key} className="px-2 py-2">
                              <input
                                type="number"
                                min="0"
                                step="1"
                                inputMode="numeric"
                                className={`${fieldClass} px-2 py-1.5 text-center`}
                                value={state[b.key]}
                                onChange={(e) =>
                                  setBucketField(setter)(b.key, e.target.value)
                                }
                              />
                            </td>
                          ))}
                          <td className="px-3 py-2.5 text-center font-semibold text-gray-700 tabular-nums">
                            {bucketTotal(state)}
                          </td>
                        </tr>
                      );
                    })}

                    <tr className="bg-gray-50">
                      <td className="px-4 py-3 font-semibold text-gray-800">
                        Closing
                      </td>
                      {BUCKETS.map((b) => (
                        <td
                          key={b.key}
                          className="px-3 py-3 text-center font-semibold text-gray-900 tabular-nums"
                        >
                          {preview.closing[b.key]}
                        </td>
                      ))}
                      <td className="px-3 py-3 text-center text-lg font-bold text-gray-900 tabular-nums">
                        {bucketTotal(preview.closing)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div
                className={`mt-4 flex items-center justify-between rounded-2xl border px-4 py-3 ${netBg}`}
              >
                <div>
                  <div className="text-sm font-semibold text-gray-700">
                    Net Value for the day
                  </div>
                  <div className="text-[11px] text-gray-500">
                    Added − Recovered − Recharge
                  </div>
                </div>
                <span className={`text-2xl font-bold tabular-nums ${netCls}`}>
                  {net > 0 ? "+" : ""}
                  {net}
                </span>
              </div>
            </>
          )}

          <div className="mt-5">
            <label className={labelClass}>Note (optional)</label>
            <textarea
              className={fieldClass}
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Anything worth remembering about this day…"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-gray-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-full border-2 border-gray-300 px-5 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-gray-600 transition hover:border-gray-400 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-full bg-amber-500 px-6 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-950 transition hover:bg-amber-400 disabled:opacity-60"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add entry"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EntryModal;
